import React, { useEffect, useState, useCallback } from 'react';
import { Table, Typography, Tag, Space, Button, Modal, Descriptions, message, Pagination } from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { SystemAuditLog, AuditLogFilters, PaginatedAuditLogResponse } from '../types';
import AuditLogFiltersComponent from '../components/AuditLogFilters';
import AuditLogStatisticsComponent from '../components/AuditLogStatistics';

const { Title } = Typography;

const SystemAuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<SystemAuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState<{visible: boolean, data: any}>({visible: false, data: null});
  const [userMap, setUserMap] = useState<{[id: number]: string}>({});
  const [assetMap, setAssetMap] = useState<{[id: number]: string}>({});
  const [employeeMap, setEmployeeMap] = useState<{[id: number]: string}>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState<AuditLogFilters>({});

  // Lấy danh sách user để map tên
  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiClient.get('/users?all=1');
      const users = Array.isArray(res) ? res : (res as any).data || res;
      const map: {[id: number]: string} = {};
      users.forEach((u: any) => { 
        map[u.id] = u.full_name || u.username || u.email || `User ${u.id}`; 
      });
      setUserMap(map);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  // Lấy danh sách tài sản để map tên
  const fetchAssets = useCallback(async () => {
    try {
      const res = await apiClient.get('/assets?all=1');
      const assets = Array.isArray(res) ? res : (res as any).data || res;
      const map: {[id: number]: string} = {};
      assets.forEach((a: any) => { 
        map[a.id] = a.name || a.asset_code || `Asset ${a.id}`; 
      });
      setAssetMap(map);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, []);

  // Lấy danh sách nhân viên để map tên
  const fetchEmployees = useCallback(async () => {
    try {
      const res = await apiClient.get('/employees?all=1');
      const employees = Array.isArray(res) ? res : (res as any).data || res;
      const map: {[id: number]: string} = {};
      employees.forEach((e: any) => { 
        map[e.id] = e.full_name || e.employee_code || `Employee ${e.id}`; 
      });
      setEmployeeMap(map);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers(); 
    fetchAssets(); 
    fetchEmployees(); 
  }, [fetchUsers, fetchAssets, fetchEmployees]);

  const fetchLogs = async (page = 1, pageSize = 20, currentFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        ...currentFilters,
        limit: pageSize,
        offset: (page - 1) * pageSize
      };

      const response = await apiClient.getSystemAuditLogs(params);
      const data = response as PaginatedAuditLogResponse;
      
      setLogs(data.data || []);
      setPagination({
        current: page,
        pageSize,
        total: data.pagination?.total || 0
      });
    } catch (error) {
      console.error('Error fetching logs:', error);
      message.error('Không thể tải dữ liệu nhật ký hệ thống');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1, pagination.pageSize, filters);
  }, [filters]);

  const handleFiltersChange = (newFilters: AuditLogFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchLogs(page, pageSize || pagination.pageSize, filters);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const blob = await apiClient.exportSystemAuditLogs(format, filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${dayjs().format('YYYY-MM-DD')}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success(`Đã xuất dữ liệu thành công (${format.toUpperCase()})`);
    } catch (error) {
      console.error('Error exporting logs:', error);
      message.error('Không thể xuất dữ liệu');
    }
  };

  const getActionTypeColor = (actionType: string) => {
    if (actionType.includes('CREATE')) return 'green';
    if (actionType.includes('UPDATE')) return 'blue';
    if (actionType.includes('DELETE')) return 'red';
    if (actionType.includes('LOGIN')) return 'purple';
    if (actionType.includes('LOGOUT')) return 'orange';
    return 'default';
  };

  const translateActionType = (actionType: string) => {
    const actionMap: { [key: string]: string } = {
      // Asset actions
      'ASSET_CREATE': 'Tạo tài sản',
      'ASSET_UPDATE': 'Cập nhật tài sản',
      'ASSET_DELETE': 'Xóa tài sản',
      'CREATE_ASSET': 'Tạo tài sản',
      'UPDATE_ASSET': 'Cập nhật tài sản',
      'DELETE_ASSET': 'Xóa tài sản',
      
      // Employee actions
      'EMPLOYEE_CREATE': 'Tạo nhân viên',
      'EMPLOYEE_UPDATE': 'Cập nhật nhân viên',
      'EMPLOYEE_DELETE': 'Xóa nhân viên',
      'CREATE_EMPLOYEE': 'Tạo nhân viên',
      'UPDATE_EMPLOYEE': 'Cập nhật nhân viên',
      'DELETE_EMPLOYEE': 'Xóa nhân viên',
      
      // User actions
      'USER_CREATE': 'Tạo người dùng',
      'USER_UPDATE': 'Cập nhật người dùng',
      'USER_DELETE': 'Xóa người dùng',
      'CREATE_USER': 'Tạo người dùng',
      'UPDATE_USER': 'Cập nhật người dùng',
      'DELETE_USER': 'Xóa người dùng',
      'PASSWORD_CHANGE': 'Đổi mật khẩu',
      
      // Company actions
      'COMPANY_CREATE': 'Tạo công ty',
      'COMPANY_UPDATE': 'Cập nhật công ty',
      'COMPANY_DELETE': 'Xóa công ty',
      'CREATE_COMPANY': 'Tạo công ty',
      'UPDATE_COMPANY': 'Cập nhật công ty',
      'DELETE_COMPANY': 'Xóa công ty',
      
      // Location actions
      'LOCATION_CREATE': 'Tạo vị trí',
      'LOCATION_UPDATE': 'Cập nhật vị trí',
      'LOCATION_DELETE': 'Xóa vị trí',
      'CREATE_LOCATION': 'Tạo vị trí',
      'UPDATE_LOCATION': 'Cập nhật vị trí',
      'DELETE_LOCATION': 'Xóa vị trí',
      
      // Supplier actions
      'SUPPLIER_CREATE': 'Tạo nhà cung cấp',
      'SUPPLIER_UPDATE': 'Cập nhật nhà cung cấp',
      'SUPPLIER_DELETE': 'Xóa nhà cung cấp',
      'CREATE_SUPPLIER': 'Tạo nhà cung cấp',
      'UPDATE_SUPPLIER': 'Cập nhật nhà cung cấp',
      'DELETE_SUPPLIER': 'Xóa nhà cung cấp',
      
      // Category actions
      'ASSET_CATEGORY_CREATE': 'Tạo danh mục tài sản',
      'ASSET_CATEGORY_UPDATE': 'Cập nhật danh mục tài sản',
      'ASSET_CATEGORY_DELETE': 'Xóa danh mục tài sản',
      'CREATE_ASSET_CATEGORY': 'Tạo danh mục tài sản',
      'UPDATE_ASSET_CATEGORY': 'Cập nhật danh mục tài sản',
      'DELETE_ASSET_CATEGORY': 'Xóa danh mục tài sản',
      
      // Auth actions
      'LOGIN': 'Đăng nhập',
      'LOGOUT': 'Đăng xuất',
      'LOGIN_FAILED': 'Đăng nhập thất bại',
      'TOKEN_REFRESH': 'Làm mới token',
      
      // Maintenance actions
      'MAINTENANCE_CREATE': 'Tạo bảo trì',
      'MAINTENANCE_UPDATE': 'Cập nhật bảo trì',
      'MAINTENANCE_DELETE': 'Xóa bảo trì',
      'CREATE_MAINTENANCE': 'Tạo bảo trì',
      'UPDATE_MAINTENANCE': 'Cập nhật bảo trì',
      'DELETE_MAINTENANCE': 'Xóa bảo trì',
      
      // Inventory audit actions
      'INVENTORY_AUDIT_CREATE': 'Tạo kiểm kê',
      'INVENTORY_AUDIT_UPDATE': 'Cập nhật kiểm kê',
      'INVENTORY_AUDIT_DELETE': 'Xóa kiểm kê',
      'CREATE_INVENTORY_AUDIT': 'Tạo kiểm kê',
      'UPDATE_INVENTORY_AUDIT': 'Cập nhật kiểm kê',
      'DELETE_INVENTORY_AUDIT': 'Xóa kiểm kê',
      
      // Disposal actions
      'DISPOSAL_REQUEST_CREATE': 'Tạo yêu cầu thanh lý',
      'DISPOSAL_REQUEST_UPDATE': 'Cập nhật yêu cầu thanh lý',
      'DISPOSAL_REQUEST_DELETE': 'Xóa yêu cầu thanh lý',
      'CREATE_DISPOSAL_REQUEST': 'Tạo yêu cầu thanh lý',
      'UPDATE_DISPOSAL_REQUEST': 'Cập nhật yêu cầu thanh lý',
      'DELETE_DISPOSAL_REQUEST': 'Xóa yêu cầu thanh lý',
      
      // Transfer actions
      'TRANSFER_REQUEST_CREATE': 'Tạo yêu cầu điều chuyển',
      'TRANSFER_REQUEST_UPDATE': 'Cập nhật yêu cầu điều chuyển',
      'TRANSFER_REQUEST_DELETE': 'Xóa yêu cầu điều chuyển',
      'CREATE_TRANSFER_REQUEST': 'Tạo yêu cầu điều chuyển',
      'UPDATE_TRANSFER_REQUEST': 'Cập nhật yêu cầu điều chuyển',
      'DELETE_TRANSFER_REQUEST': 'Xóa yêu cầu điều chuyển',
    };
    
    return actionMap[actionType] || actionType;
  };

  const translateEntityType = (entityType: string) => {
    const entityMap: { [key: string]: string } = {
      'Asset': 'Tài sản',
      'Employee': 'Nhân viên',
      'User': 'Người dùng',
      'Company': 'Công ty',
      'Location': 'Vị trí',
      'Supplier': 'Nhà cung cấp',
      'AssetCategory': 'Danh mục tài sản',
      'Maintenance': 'Bảo trì',
      'MaintenanceType': 'Loại bảo trì',
      'MaintenanceCampaign': 'Chiến dịch bảo trì',
      'MaintenanceCampaignItem': 'Mục chiến dịch bảo trì',
      'InventoryAudit': 'Kiểm kê',
      'InventoryAuditDetail': 'Chi tiết kiểm kê',
      'DisposalRequest': 'Yêu cầu thanh lý',
      'DisposalRequestItem': 'Mục yêu cầu thanh lý',
      'DisposalRequestDocument': 'Tài liệu yêu cầu thanh lý',
      'RepairRequest': 'Yêu cầu sửa chữa',
      'TransferRequest': 'Yêu cầu điều chuyển',
      'AssetAccessory': 'Phụ kiện tài sản',
      'AssetAttachment': 'Tệp đính kèm tài sản',
    };
    
    return entityMap[entityType] || entityType;
  };

  const getEntityName = (entityType: string, entityId: number, details: any) => {
    if (entityType === 'Asset') {
      if (assetMap[entityId]) return assetMap[entityId];
      const name = details?.new?.name || details?.old?.name;
      if (name) return name;
      return `Tài sản ${entityId}`;
    }
    if (entityType === 'Employee') {
      if (employeeMap[entityId]) return employeeMap[entityId];
      const name = details?.new?.full_name || details?.old?.full_name;
      if (name) return name;
      return `Nhân viên ${entityId}`;
    }
    if (entityType === 'User') {
      if (userMap[entityId]) return userMap[entityId];
      return `Người dùng ${entityId}`;
    }
    if (entityType === 'Company') {
      return `Công ty ${entityId}`;
    }
    if (entityType === 'Location') {
      return `Vị trí ${entityId}`;
    }
    if (entityType === 'Supplier') {
      return `Nhà cung cấp ${entityId}`;
    }
    if (entityType === 'AssetCategory') {
      return `Danh mục tài sản ${entityId}`;
    }
    if (entityType === 'Maintenance') {
      return `Bảo trì ${entityId}`;
    }
    if (entityType === 'MaintenanceType') {
      return `Loại bảo trì ${entityId}`;
    }
    if (entityType === 'MaintenanceCampaign') {
      return `Chiến dịch bảo trì ${entityId}`;
    }
    if (entityType === 'MaintenanceCampaignItem') {
      return `Mục chiến dịch bảo trì ${entityId}`;
    }
    if (entityType === 'InventoryAudit') {
      return `Kiểm kê ${entityId}`;
    }
    if (entityType === 'InventoryAuditDetail') {
      return `Chi tiết kiểm kê ${entityId}`;
    }
    if (entityType === 'DisposalRequest') {
      return `Yêu cầu thanh lý ${entityId}`;
    }
    if (entityType === 'DisposalRequestItem') {
      return `Mục yêu cầu thanh lý ${entityId}`;
    }
    if (entityType === 'DisposalRequestDocument') {
      return `Tài liệu yêu cầu thanh lý ${entityId}`;
    }
    if (entityType === 'RepairRequest') {
      return `Yêu cầu sửa chữa ${entityId}`;
    }
    if (entityType === 'TransferRequest') {
      return `Yêu cầu điều chuyển ${entityId}`;
    }
    if (entityType === 'AssetAccessory') {
      return `Phụ kiện tài sản ${entityId}`;
    }
    if (entityType === 'AssetAttachment') {
      return `Tệp đính kèm tài sản ${entityId}`;
    }
    return `${translateEntityType(entityType)} ${entityId}`;
  };

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
      render: (timestamp: string) => dayjs(timestamp).format('DD/MM/YYYY HH:mm:ss')
    },
         {
       title: 'Người dùng',
       dataIndex: 'user_id',
       key: 'user_id',
       width: 120,
       render: (userId: number) => 
         userId ? (
           <Tag color="blue">
             {userMap[userId] || `User ${userId}`}
           </Tag>
         ) : (
           <Tag>Hệ thống</Tag>
         )
     },
         {
       title: 'Hành động',
       dataIndex: 'action_type',
       key: 'action_type',
       width: 150,
       render: (actionType: string) => (
         <Tag color={getActionTypeColor(actionType)}>
           {translateActionType(actionType)}
         </Tag>
       )
     },
         {
       title: 'Đối tượng',
       dataIndex: 'target_entity_type',
       key: 'target_entity_type',
       width: 120,
       render: (entityType: string) => entityType ? translateEntityType(entityType) : '—'
     },
         {
       title: 'Tên đối tượng',
       dataIndex: 'target_entity_id',
       key: 'target_entity_id',
       width: 150,
       render: (entityId: number, record: SystemAuditLog) => {
         if (!entityId) return '—';
         return getEntityName(record.target_entity_type || '', entityId, record.details);
       }
     },
    {
      title: 'Chi tiết',
      key: 'details',
      width: 100,
      render: (record: SystemAuditLog) => (
                 <Button
           size="small"
           icon={<EyeOutlined />}
           onClick={() => setDetailModal({visible: true, data: record.details})}
           disabled={!record.details}
           title="Xem chi tiết"
         />
      )
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 120,
      render: (ip: string) => ip || '—'
    }
  ];

  const translateFieldName = (fieldName: string) => {
    const fieldMap: { [key: string]: string } = {
      // Common fields
      'id': 'ID',
      'created_at': 'Ngày tạo',
      'updated_at': 'Ngày cập nhật',
      'public_uuid': 'UUID',
      
      // User fields
      'username': 'Tên đăng nhập',
      'email': 'Email',
      'role': 'Vai trò',
      'company_id': 'ID Công ty',
      
      // Employee fields
      'full_name': 'Họ và tên',
      'employee_code': 'Mã nhân viên',
      'department': 'Phòng ban',
      
      // Asset fields
      'name': 'Tên tài sản',
      'asset_code': 'Mã tài sản',
      'description': 'Mô tả',
      'technical_specifications': 'Thông số kỹ thuật',
      'technical_condition': 'Tình trạng kỹ thuật',
      'category_id': 'ID Danh mục',
      'purchase_date': 'Ngày mua',
      'purchase_cost': 'Giá mua',
      'warranty_period_months': 'Thời hạn bảo hành (tháng)',
      'warranty_expiry_date': 'Ngày hết hạn bảo hành',
      'last_audit_date': 'Ngày kiểm kê cuối',
      'audit_cycle_months': 'Chu kỳ kiểm kê (tháng)',
      'status': 'Trạng thái',
      'supplier_id': 'ID Nhà cung cấp',
      'assigned_to_employee_id': 'ID Nhân viên được giao',
      'location_id': 'ID Vị trí',
      'asset_approval_status': 'Trạng thái phê duyệt',
      'approved_by_user_id': 'ID Người phê duyệt',
      'approval_date': 'Ngày phê duyệt',
      'approver_notes': 'Ghi chú phê duyệt',
      'parent_asset_id': 'ID Tài sản cha',
      
      // Company fields
      'company_code': 'Mã công ty',
      'address': 'Địa chỉ',
      'tax_code': 'Mã số thuế',
      
      // Location fields
      'parent_location_id': 'ID Vị trí cha',
      
      // Supplier fields
      'contact_phone': 'Số điện thoại liên hệ',
      
      // Category fields
      
      // Maintenance fields
      'maintenance_type_id': 'ID Loại bảo trì',
      'maintenance_date': 'Ngày bảo trì',
      'maintenance_cost': 'Chi phí bảo trì',
      'maintenance_notes': 'Ghi chú bảo trì',
      'maintenance_status': 'Trạng thái bảo trì',
      
      // Inventory audit fields
      'audit_date': 'Ngày kiểm kê',
      'audit_status': 'Trạng thái kiểm kê',
      'audit_notes': 'Ghi chú kiểm kê',
      
      // Disposal fields
      'disposal_date': 'Ngày thanh lý',
      'disposal_reason': 'Lý do thanh lý',
      'disposal_method': 'Phương thức thanh lý',
      'disposal_value': 'Giá trị thanh lý',
      
      // Transfer fields
      'transfer_date': 'Ngày điều chuyển',
      'from_location_id': 'ID Vị trí từ',
      'to_location_id': 'ID Vị trí đến',
      'transfer_reason': 'Lý do điều chuyển',
      
      // Repair fields
      'repair_date': 'Ngày sửa chữa',
      'repair_cost': 'Chi phí sửa chữa',
      'repair_notes': 'Ghi chú sửa chữa',
      'repair_status': 'Trạng thái sửa chữa',
    };
    
    return fieldMap[fieldName] || fieldName;
  };

  const renderDetailsTable = (obj: any) => {
    if (!obj || typeof obj !== 'object') return null;
    return Object.entries(obj)
      .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && Object.keys(v).length === 0))
      .map(([k, v]) =>
        typeof v === 'object'
          ? (
            <Descriptions.Item key={k} label={translateFieldName(k)}>
              <Descriptions column={1} size="small" bordered>
                {renderDetailsTable(v)}
              </Descriptions>
            </Descriptions.Item>
          )
          : (
            <Descriptions.Item key={k} label={translateFieldName(k)}>
              {String(v)}
            </Descriptions.Item>
          )
      );
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        Nhật ký hệ thống
      </Title>

      {/* Thống kê */}
      <AuditLogStatisticsComponent />

      {/* Bộ lọc */}
      <AuditLogFiltersComponent
        onFiltersChange={handleFiltersChange}
        onExport={handleExport}
        loading={loading}
      />

      {/* Bảng dữ liệu */}
      <div style={{ background: '#fff', padding: 16, borderRadius: 8 }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            Tổng cộng: <strong>{pagination.total}</strong> bản ghi
          </span>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchLogs(pagination.current, pagination.pageSize, filters)}
            loading={loading}
            title="Làm mới"
          />
        </div>

        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1000 }}
          size="small"
        />

        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
          />
        </div>
      </div>

      {/* Modal chi tiết */}
      <Modal
        open={detailModal.visible}
        onCancel={() => setDetailModal({visible: false, data: null})}
        footer={null}
        width={800}
        title="Chi tiết thao tác"
      >
        <Descriptions column={1} bordered size="small">
          {detailModal.data?.new && (
            <>
              <Descriptions.Item label="Dữ liệu mới" span={1}>
                <Descriptions column={1} bordered size="small">
                  {renderDetailsTable(detailModal.data.new)}
                </Descriptions>
              </Descriptions.Item>
            </>
          )}
          {detailModal.data?.old && (
            <>
              <Descriptions.Item label="Dữ liệu cũ" span={1}>
                <Descriptions column={1} bordered size="small">
              {renderDetailsTable(detailModal.data.old)}
                </Descriptions>
              </Descriptions.Item>
            </>
          )}
                     {detailModal.data?.updated_fields && (
             <Descriptions.Item label="Các trường đã thay đổi">
               {detailModal.data.updated_fields.map((field: string) => translateFieldName(field)).join(', ')}
             </Descriptions.Item>
           )}
          {!detailModal.data?.new && !detailModal.data?.old && renderDetailsTable(detailModal.data)}
        </Descriptions>
      </Modal>
    </div>
  );
};

export default SystemAuditLogPage; 